import * as Sentry from '@sentry/nextjs';

import { PAYMENT_TYPE_UNION } from '@boss/services/client';

/**
 * Get payment status request
 *
 * @async
 * @returns {Promise}
 * @param payId
 */
const getPaymentStatus = async (payId: string, locale: string) => {
  try {
    const paymentRequest = await fetch(`/api/payment${payId}?locale=${locale}`, {
      method: 'POST',
    });

    return await paymentRequest.json();
  } catch (error) {
    console.error(error);

    Sentry.captureException(error, {
      tags: {
        type: 'Get payment status',
      },
    });

    throw error;
  }
};

/**
 * Create payment request
 *
 * @async
 * @returns {Promise}
 * @param type
 * @param locale
 * @param referenceId
 * @param amount as float
 * @param accountId
 * @param storeServiceId
 */
const createPaymentRequest = async ({
  type,
  locale,
  referenceId,
  amount,
  accountId,
  storeServiceId,
  invoiceId,
  description,
}: {
  type: PAYMENT_TYPE_UNION;
  locale: string;
  referenceId: string;
  amount: number; //float
  accountId?: string;
  storeServiceId?: string;
  invoiceId?: string;
  description?: string;
}) => {
  try {
    const paymentRequest = await fetch(`/api/payment?locale=${locale}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: type,
        referenceId,
        locale: locale,
        amount: amount,
        accountId,
        storeServiceId,
        invoiceId,
        description,
      }),
    });

    if (!paymentRequest.ok) {
      const errorBody = await paymentRequest.json();

      // Return the parsed error body (including `code`) instead of throwing so that
      // callers can detect a 1001 amount-mismatch and react with the "basket changed"
      // flow. A thrown generic Error stripped that code and the guard never triggered.
      console.error(errorBody);

      Sentry.captureException(new Error(errorBody.error ?? 'Payment request failed'), {
        tags: {
          type: 'Creating payment request',
        },
        extra: {
          code: errorBody.code,
        },
      });

      return errorBody;
    }

    return await paymentRequest.json();
  } catch (error) {
    console.error(error);

    Sentry.captureException(error, {
      tags: {
        type: 'Creating payment request',
      },
    });

    throw error;
  }
};

export { getPaymentStatus, createPaymentRequest };
